import { useEffect, useState, useRef } from "react";
import { motion } from "framer-motion";
import { ChevronDown } from "lucide-react";
import { siteData } from "../data/mock";

export const HeroSection = () => {
  const [mousePos, setMousePos] = useState({ x: 0, y: 0 });
  const heroRef = useRef(null);

  useEffect(() => {
    const handleMouseMove = (e) => {
      if (heroRef.current) {
        const rect = heroRef.current.getBoundingClientRect();
        setMousePos({
          x: (e.clientX - rect.left) / rect.width - 0.5,
          y: (e.clientY - rect.top) / rect.height - 0.5
        });
      }
    };
    window.addEventListener("mousemove", handleMouseMove);
    return () => window.removeEventListener("mousemove", handleMouseMove);
  }, []);

  const scrollTo = (id) => {
    const el = document.querySelector(id);
    if (el) el.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <section
      id="home"
      ref={heroRef}
      className="relative min-h-screen flex items-center justify-center overflow-hidden"
    >
      {/* Background Image with Parallax */}
      <div
        className="absolute inset-0 z-0"
        style={{
          transform: `translate(${mousePos.x * -10}px, ${mousePos.y * -10}px) scale(1.05)`
        }}
      >
        <img
          src="/images/interior-3.jpg"
          alt=""
          className="w-full h-full object-cover"
        />
      </div>

      {/* Dark Overlay */}
      <div className="absolute inset-0 z-[1] bg-gradient-to-b from-[#0d0b08]/85 via-[#0d0b08]/75 to-[#0d0b08]/95" />

      {/* Decorative Gold Line */}
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[1px] h-32 z-[2] bg-gradient-to-b from-transparent via-gold/40 to-transparent" />

      {/* Content */}
      <div className="relative z-10 text-center px-6 max-w-4xl mx-auto">
        {/* Logo */}
        <motion.div
          initial={{ opacity: 0, scale: 0.8 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 1, ease: "easeOut" }}
          className="mb-8"
        >
          <img
            src="/images/logo.jpg"
            alt="Split Decisions"
            className="w-36 h-36 md:w-44 md:h-44 rounded-full object-cover mx-auto border-2 border-gold/30 shadow-2xl shadow-gold/10"
          />
        </motion.div>

        {/* Tagline */}
        <motion.h1
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.3 }}
          className="font-heading font-bold text-4xl sm:text-5xl md:text-6xl lg:text-7xl text-cream mb-6 leading-tight"
        >
          {siteData.brand.heroTagline}
        </motion.h1>

        {/* Subheading */}
        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.6 }}
          className="text-cream/60 text-base sm:text-lg md:text-xl max-w-2xl mx-auto mb-12 font-light leading-relaxed"
        >
          {siteData.brand.heroSubheading}
        </motion.p>

        {/* Dual CTA Buttons - the "split decision" */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.9 }}
          className="flex flex-col sm:flex-row items-center justify-center gap-4 sm:gap-6"
        >
          {/* Left CTA */}
          <button
            onClick={() => scrollTo("#visit")}
            className="group relative px-8 py-4 font-medium uppercase tracking-[0.15em] text-sm rounded-sm overflow-hidden min-w-[220px]"
            style={{ backgroundColor: '#c9a96e', color: '#0d0b08' }}
            onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = '#dcc48f'; e.currentTarget.style.boxShadow = '0 10px 30px rgba(201,169,110,0.25)'; }}
            onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = '#c9a96e'; e.currentTarget.style.boxShadow = 'none'; }}
          >
            <span className="relative z-10">{siteData.brand.ctaLeft}</span>
          </button>

          {/* Divider */}
          <span className="hidden sm:block text-xs uppercase tracking-[0.3em] font-light" style={{ color: 'rgba(201,169,110,0.4)' }}>or</span>

          {/* Right CTA */}
          <button
            onClick={() => scrollTo("#gallery")}
            className="group px-8 py-4 font-medium uppercase tracking-[0.15em] text-sm rounded-sm min-w-[220px]"
            style={{ border: '1px solid rgba(201,169,110,0.4)', color: '#c9a96e', backgroundColor: 'transparent' }}
            onMouseEnter={(e) => { e.currentTarget.style.borderColor = '#c9a96e'; e.currentTarget.style.backgroundColor = 'rgba(201,169,110,0.1)'; e.currentTarget.style.boxShadow = '0 10px 30px rgba(201,169,110,0.15)'; }}
            onMouseLeave={(e) => { e.currentTarget.style.borderColor = 'rgba(201,169,110,0.4)'; e.currentTarget.style.backgroundColor = 'transparent'; e.currentTarget.style.boxShadow = 'none'; }}
          >
            <span>{siteData.brand.ctaRight}</span>
          </button>
        </motion.div>

        {/* Location Badge */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1.3 }}
          className="mt-16 text-cream/40 text-xs uppercase tracking-[0.25em] font-light"
        >
          Ansonia, Connecticut
        </motion.div>
      </div>

      {/* Scroll Indicator */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.5 }}
        className="absolute bottom-8 left-1/2 -translate-x-1/2 z-10"
      >
        <motion.div
          animate={{ y: [0, 8, 0] }}
          transition={{ repeat: Infinity, duration: 2 }}
        >
          <ChevronDown className="text-gold/40" size={24} />
        </motion.div>
      </motion.div>
    </section>
  );
};
